import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, MessageSquare, Hammer, Brush, Wrench, Layers,
  ArrowRight, ArrowUpRight, ShieldCheck, Trees, Award, CheckCircle2,
  Menu, X, Clock, Upload, Trash2,
  Bug, Search, ClipboardCheck, Beaker, Sparkles, AlertTriangle,
} from "lucide-react";
import { useT } from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO = "https://customer-assets.emergentagent.com/job_maid-service-6/artifacts/elfl7tug_F736BDB4-A6C3-463A-9B02-CDF53E55EE0B.png";
const CABIN_HERO = "https://customer-assets.emergentagent.com/job_maid-service-6/artifacts/63r8n8qi_D4F90641-C093-4731-8318-287D6F0FD357.png";
const BEFORE_IMG = "https://customer-assets.emergentagent.com/job_maid-service-6/artifacts/zemv17xa_IMG_9019.jpeg";
const AFTER_IMG = "https://customer-assets.emergentagent.com/job_maid-service-6/artifacts/tkwd0kn5_IMG_9021.png";

const CONTACT = {
  phone: "450-820-8228",
  phoneHref: "tel:+14508208228",
  smsHref: "sms:+14508208228",
  email: "aglboisrond.inc@gmail.com",
  emailHref: "mailto:aglboisrond.inc@gmail.com",
};

// Static city list — same in both langs
const ZONES = [
  "Sainte-Adèle", "Saint-Sauveur", "Mont-Tremblant", "Sainte-Agathe",
  "Saint-Jérôme", "Mirabel", "Lachute", "Mont-Laurier",
];

const SERVICE_KEYS = ["sablage", "teinture", "reparation", "calfeutrage"];
const SERVICE_ICONS = { sablage: Hammer, teinture: Brush, reparation: Wrench, calfeutrage: Layers };
const VALUE_ITEMS = [
  { icon: Trees, key: "specialists" },
  { icon: ShieldCheck, key: "warranty" },
  { icon: Award, key: "detail" },
  { icon: MapPin, key: "mobility" },
];

// =================== Language Switch ===================
const LangSwitch = ({ compact = false }) => {
  const { lang, setLang } = useT();
  return (
    <div className={`inline-flex items-center rounded-sm border border-[#C8A258]/30 bg-[#15130E] overflow-hidden ${compact ? "" : ""}`}
      data-testid="lang-switch">
      <button
        onClick={() => setLang("fr")}
        className={`px-3 py-1.5 text-xs tracking-wider transition-colors ${
          lang === "fr" ? "bg-[#C8A258] text-[#0B0A08]" : "text-[#EDE3CF]/70 hover:text-[#C8A258]"
        }`}
        data-testid="lang-fr"
        aria-label="Français"
      >
        FR
      </button>
      <button
        onClick={() => setLang("en")}
        className={`px-3 py-1.5 text-xs tracking-wider transition-colors ${
          lang === "en" ? "bg-[#C8A258] text-[#0B0A08]" : "text-[#EDE3CF]/70 hover:text-[#C8A258]"
        }`}
        data-testid="lang-en"
        aria-label="English"
      >
        EN
      </button>
    </div>
  );
};

// =================== Nav ===================
const Nav = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#services", l: t("nav.services") },
    { href: "#prevention", l: t("nav.prevention") },
    { href: "#galerie", l: t("nav.gallery") },
    { href: "#processus", l: t("nav.process") },
    { href: "#zones", l: t("nav.zones") },
    { href: "#contact", l: t("nav.contact") },
  ];
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-[#C8A258]/15"
      style={{ background: "rgba(11,10,8,0.78)" }} data-testid="site-nav">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3 min-w-0" data-testid="nav-logo-link">
          <img src={LOGO} alt="AGL Bois Rond" className="h-12 w-12 object-contain shrink-0" />
          <div className="leading-tight min-w-0">
            <div className="font-display text-xl tracking-tight text-[#EDE3CF] truncate">
              AGL <span className="text-[#C8A258]">Bois Rond</span> {t("nav.brand_suffix")}
            </div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#C8A258]/80 truncate">
              {t("nav.tagline")}
            </div>
          </div>
        </a>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a key={l.href} href={l.href}
              className="text-sm tracking-wide text-[#EDE3CF]/80 hover:text-[#C8A258] transition-colors"
              data-testid={`nav-link-${l.href.slice(1)}`}>{l.l}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <LangSwitch />
          <a href={CONTACT.phoneHref} data-testid="nav-call-btn">
            <Button variant="outline" className="rounded-sm border-[#C8A258]/40 bg-transparent text-[#EDE3CF] hover:bg-[#C8A258]/10 hover:text-[#C8A258]">
              <Phone className="w-4 h-4 mr-2" /> {CONTACT.phone}
            </Button>
          </a>
          <a href="#contact" data-testid="nav-quote-btn">
            <Button className="rounded-sm bg-[#C8A258] text-[#0B0A08] hover:bg-[#B0894A]">
              {t("nav.cta_quote")}
            </Button>
          </a>
        </div>
        <div className="flex md:hidden items-center gap-2">
          <LangSwitch compact />
          <button onClick={() => setOpen(!open)} aria-label={t("nav.menu")} data-testid="mobile-menu-toggle"
            className="inline-flex items-center justify-center w-10 h-10 rounded-sm border border-[#C8A258]/30 text-[#C8A258]">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#C8A258]/15 bg-[#0B0A08]">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                className="text-[#EDE3CF] text-base" data-testid={`mobile-nav-${l.href.slice(1)}`}>{l.l}</a>
            ))}
            <div className="flex gap-3 pt-2">
              <a href={CONTACT.phoneHref} className="flex-1">
                <Button variant="outline" className="w-full rounded-sm border-[#C8A258]/40 bg-transparent text-[#EDE3CF]">
                  <Phone className="w-4 h-4 mr-2" /> {CONTACT.phone}
                </Button>
              </a>
              <a href="#contact" className="flex-1" onClick={() => setOpen(false)}>
                <Button className="w-full rounded-sm bg-[#C8A258] text-[#0B0A08]">{t("nav.cta_quote")}</Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// =================== Hero ===================
const Hero = () => {
  const { t } = useT();
  return (
    <section id="top" className="relative overflow-hidden grain">
      <div className="absolute inset-0">
        <img src={CABIN_HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(115deg, rgba(11,10,8,0.96) 0%, rgba(11,10,8,0.78) 45%, rgba(11,10,8,0.35) 100%)" }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-32 md:pt-32 md:pb-44 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 fade-up">
          <div className="inline-flex items-center gap-3 text-[#C8A258] text-xs tracking-[0.3em] uppercase mb-6">
            <span className="w-10 h-px bg-[#C8A258]" /> {t("hero.eyebrow")}
          </div>
          <h1 className="font-display text-[#EDE3CF] text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight" data-testid="hero-title">
            {t("hero.title_l1")}<br /><em className="text-[#C8A258] font-medium not-italic">{t("hero.title_l2")}</em>
          </h1>
          <p className="mt-6 max-w-xl text-[#EDE3CF]/75 text-lg font-light leading-relaxed">{t("hero.lead")}</p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#contact" data-testid="hero-cta-quote">
              <Button size="lg" className="rounded-sm bg-[#C8A258] text-[#0B0A08] hover:bg-[#B0894A] h-12 px-7 text-base">
                {t("hero.cta_quote")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href={CONTACT.phoneHref} data-testid="hero-cta-call">
              <Button size="lg" variant="outline"
                className="rounded-sm bg-transparent border-[#EDE3CF]/30 text-[#EDE3CF] hover:bg-[#EDE3CF]/10 hover:text-[#EDE3CF] h-12 px-7 text-base">
                <Phone className="w-4 h-4 mr-2" /> {CONTACT.phone}
              </Button>
            </a>
            <a href={CONTACT.smsHref} className="text-[#EDE3CF]/80 hover:text-[#C8A258] inline-flex items-center gap-2" data-testid="hero-cta-sms">
              <MessageSquare className="w-4 h-4" /> {t("hero.cta_sms")}
            </a>
          </div>
        </div>
        <div className="md:col-span-5 flex md:justify-end fade-up">
          <div className="relative w-full max-w-sm aspect-square rounded-sm overflow-hidden border border-[#C8A258]/20" style={{ background: "#000" }}>
            <img src={LOGO} alt="Logo AGL Bois Rond Inc." className="w-full h-full object-contain p-6" />
            <div className="absolute bottom-0 inset-x-0 p-4 text-center text-[#C8A258]/90 text-[10px] tracking-[0.3em] uppercase border-t border-[#C8A258]/15">
              {t("hero.logo_caption")}
            </div>
          </div>
        </div>
      </div>
      <div className="relative border-t border-[#C8A258]/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-5 grid grid-cols-2 md:grid-cols-4 gap-6 text-[#EDE3CF]/80 text-sm">
          {[
            { I: ShieldCheck, l: t("hero.badge_warranty") },
            { I: Trees, l: t("hero.badge_specialist") },
            { I: Award, l: t("hero.badge_premium") },
            { I: MapPin, l: t("hero.badge_zone") },
          ].map((it) => (
            <div key={it.l} className="flex items-center gap-3">
              <it.I className="w-4 h-4 text-[#C8A258]" /> <span>{it.l}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =================== Services ===================
const Services = () => {
  const { t } = useT();
  return (
    <section id="services" className="py-24 md:py-32" style={{ background: "#0B0A08" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.3em] uppercase text-[#C8A258] mb-4">{t("services.eyebrow")}</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#EDE3CF] tracking-tight leading-[1.02]">
              {t("services.title_l1")}<br /><em className="text-[#C8A258] not-italic">{t("services.title_l2")}</em>
            </h2>
          </div>
          <p className="md:col-span-5 text-[#EDE3CF]/70 text-base leading-relaxed">{t("services.intro")}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#C8A258]/15 border border-[#C8A258]/15">
          {SERVICE_KEYS.map((k, i) => {
            const Icon = SERVICE_ICONS[k];
            return (
              <article key={k} className="bg-[#0B0A08] p-8 md:p-10 group hover:bg-[#15130E] transition-colors min-h-[360px] flex flex-col"
                data-testid={`service-card-${k}`}>
                <div className="flex items-start justify-between mb-6">
                  <Icon className="w-9 h-9 text-[#C8A258]" />
                  <span className="font-display text-3xl text-[#C8A258]/40">0{i + 1}</span>
                </div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#C8A258] mb-2">{t(`service.${k}.sub`)}</div>
                <h3 className="font-display text-2xl text-[#EDE3CF] mb-3">{t(`service.${k}.title`)}</h3>
                <p className="text-sm text-[#EDE3CF]/65 leading-relaxed mb-5">{t(`service.${k}.desc`)}</p>
                <ul className="space-y-1.5 mt-auto">
                  {[1, 2, 3].map((n) => (
                    <li key={n} className="text-sm text-[#EDE3CF]/85 flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#C8A258]" /> {t(`service.${k}.b${n}`)}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 pt-8 border-t border-[#C8A258]/15">
          <p className="text-sm text-[#EDE3CF]/65">{t("services.custom_lead")}</p>
          <a href="#contact" data-testid="services-cta">
            <Button variant="outline" className="rounded-sm border-[#C8A258]/40 bg-transparent text-[#C8A258] hover:bg-[#C8A258]/10">
              {t("services.custom_cta")} <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

const VERMIN_BANNER = "https://customer-assets-agu9un31.emergentagent.net/job_maid-service-6/artifacts/asreiygw_IMG_0048.jpeg";

// =================== Vermin Prevention ===================
const VerminPrevention = () => {
  const { t } = useT();
  const signs = [
    { icon: AlertTriangle, key: "sign1" },
    { icon: Sparkles, key: "sign2" },
    { icon: Search, key: "sign3" },
  ];
  const insects = ["insect1", "insect2", "insect3", "insect4"];
  const approach = [
    { icon: Search, key: "approach1" },
    { icon: ClipboardCheck, key: "approach2" },
    { icon: Beaker, key: "approach3" },
    { icon: ShieldCheck, key: "approach4" },
  ];
  return (
    <section id="prevention" className="py-24 md:py-32 relative overflow-hidden grain"
      style={{ background: "linear-gradient(180deg, #0B0A08 0%, #15130E 100%)" }}
      data-testid="vermin-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">

        {/* Header */}
        <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-3 text-[#C8A258] text-xs tracking-[0.3em] uppercase mb-4">
              <Bug className="w-4 h-4" />
              {t("vermin.eyebrow")}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#EDE3CF] tracking-tight leading-[1.02]">
              {t("vermin.title_l1")}<br />
              <em className="text-[#C8A258] not-italic">{t("vermin.title_l2")}</em>
            </h2>
          </div>
          <p className="md:col-span-5 text-[#EDE3CF]/70 leading-relaxed">
            {t("vermin.intro")}
          </p>
        </div>

        {/* Promotional banner — professional visual */}
        <div className="mb-16 relative rounded-sm overflow-hidden border border-[#C8A258]/25 group" data-testid="vermin-banner">
          <img
            src={VERMIN_BANNER}
            alt="Des petits trous dans votre bois ? Ne les ignorez pas."
            className="w-full h-auto object-cover transition-transform duration-1000 group-hover:scale-[1.01]"
            loading="lazy"
          />
        </div>

        {/* Warning signs */}
        <div className="mb-16">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C8A258] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C8A258]" />
            {t("vermin.signs_title")}
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[#C8A258]/15 border border-[#C8A258]/15">
            {signs.map((s, i) => (
              <div key={s.key} className="bg-[#0B0A08] p-8 md:p-10 flex flex-col gap-5" data-testid={`vermin-sign-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-sm border border-[#C8A258]/40 flex items-center justify-center bg-[#C8A258]/5">
                    <s.icon className="w-6 h-6 text-[#C8A258]" />
                  </div>
                  <span className="font-display text-3xl text-[#C8A258]/30">0{i + 1}</span>
                </div>
                <h3 className="font-display text-xl text-[#EDE3CF]">{t(`vermin.${s.key}.t`)}</h3>
                <p className="text-sm text-[#EDE3CF]/65 leading-relaxed">{t(`vermin.${s.key}.d`)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Culprits */}
        <div className="mb-16">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C8A258] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C8A258]" />
            {t("vermin.insects_title")}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {insects.map((k, i) => (
              <article key={k} className="bg-[#15130E] border border-[#C8A258]/15 rounded-sm p-6 hover:border-[#C8A258]/40 transition-colors"
                data-testid={`vermin-insect-${i + 1}`}>
                <div className="flex items-start gap-3 mb-4">
                  <Bug className="w-5 h-5 text-[#C8A258] shrink-0 mt-0.5" />
                  <h4 className="font-display text-lg text-[#EDE3CF] leading-tight">{t(`vermin.${k}.t`)}</h4>
                </div>
                <p className="text-xs text-[#EDE3CF]/65 leading-relaxed">{t(`vermin.${k}.d`)}</p>
              </article>
            ))}
          </div>
        </div>

        {/* Our approach */}
        <div className="mb-14">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C8A258] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C8A258]" />
            {t("vermin.approach_title")}
          </div>
          <div className="grid md:grid-cols-4 gap-px bg-[#C8A258]/15 border border-[#C8A258]/15">
            {approach.map((a, i) => (
              <div key={a.key} className="bg-[#15130E] p-7 md:p-8 flex flex-col gap-4 min-h-[220px]"
                data-testid={`vermin-approach-${i + 1}`}>
                <a.icon className="w-7 h-7 text-[#C8A258]" />
                <h4 className="font-display text-lg text-[#EDE3CF] leading-tight">{t(`vermin.${a.key}.t`)}</h4>
                <p className="text-xs text-[#EDE3CF]/65 leading-relaxed">{t(`vermin.${a.key}.d`)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA banner */}
        <div className="border border-[#C8A258]/30 rounded-sm p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 bg-gradient-to-r from-[#C8A258]/10 to-transparent">
          <div className="flex items-start md:items-center gap-4 flex-1">
            <AlertTriangle className="w-8 h-8 text-[#C8A258] shrink-0" />
            <p className="text-[#EDE3CF] text-base md:text-lg leading-relaxed">{t("vermin.cta_lead")}</p>
          </div>
          <a href="#contact" data-testid="vermin-cta-btn" className="shrink-0">
            <Button className="rounded-sm bg-[#C8A258] text-[#0B0A08] hover:bg-[#B0894A] h-12 px-6">
              {t("vermin.cta_btn")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// =================== Before/After ===================
const BeforeAfter = () => {
  const { t } = useT();
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const drag = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(p);
  };
  return (
    <div ref={ref}
      className="relative w-full aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-sm border border-[#C8A258]/20 select-none cursor-ew-resize"
      onMouseMove={(e) => e.buttons === 1 && drag(e)}
      onTouchMove={drag} onClick={drag} data-testid="before-after-slider">
      <img src={AFTER_IMG} alt={t("gallery.after")} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={BEFORE_IMG} alt={t("gallery.before")} className="absolute inset-0 h-full object-cover"
          style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} />
      </div>
      <div className="ba-handle" style={{ left: `${pos}%` }} />
      <div className="absolute top-4 left-4 px-3 py-1 bg-[#0B0A08]/85 text-[#EDE3CF] text-[10px] tracking-[0.24em] uppercase border border-[#C8A258]/30">{t("gallery.before")}</div>
      <div className="absolute top-4 right-4 px-3 py-1 bg-[#C8A258] text-[#0B0A08] text-[10px] tracking-[0.24em] uppercase">{t("gallery.after")}</div>
      <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-0 w-full opacity-0 cursor-ew-resize" />
    </div>
  );
};

const Gallery = () => {
  const { t } = useT();
  return (
    <section id="galerie" className="py-24 md:py-32 relative overflow-hidden grain"
      style={{ background: "linear-gradient(180deg, #15130E 0%, #0B0A08 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 mb-14 items-end">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.3em] uppercase text-[#C8A258] mb-4">{t("gallery.eyebrow")}</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#EDE3CF] tracking-tight leading-[1.02]">
              {t("gallery.title_l1")}<br /><em className="text-[#C8A258] not-italic">{t("gallery.title_l2")}</em>
            </h2>
          </div>
          <p className="md:col-span-5 text-[#EDE3CF]/70 leading-relaxed">{t("gallery.intro")}</p>
        </div>
        <BeforeAfter />
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          <figure className="md:col-span-2 relative rounded-sm overflow-hidden border border-[#C8A258]/20 group">
            <img src={CABIN_HERO} alt="" className="w-full h-full object-cover aspect-[16/10] transition-transform duration-700 group-hover:scale-105" />
            <figcaption className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-[#0B0A08]/90 to-transparent">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#C8A258]">{t("gallery.realisation")}</div>
              <div className="font-display text-xl text-[#EDE3CF]">{t("gallery.cabin_caption")}</div>
            </figcaption>
          </figure>
          <div className="grid grid-cols-1 gap-5">
            <figure className="relative rounded-sm overflow-hidden border border-[#C8A258]/20 group">
              <img src={BEFORE_IMG} alt={t("gallery.before")} className="w-full h-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#0B0A08]/85 text-[#EDE3CF] text-[10px] tracking-[0.24em] uppercase border border-[#C8A258]/30">{t("gallery.before")}</span>
            </figure>
            <figure className="relative rounded-sm overflow-hidden border border-[#C8A258]/20 group">
              <img src={AFTER_IMG} alt={t("gallery.after")} className="w-full h-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#C8A258] text-[#0B0A08] text-[10px] tracking-[0.24em] uppercase">{t("gallery.after")}</span>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
};

// =================== Values, Process, Zones ===================
const Values = () => {
  const { t } = useT();
  return (
    <section className="py-24 md:py-32" style={{ background: "#0B0A08" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C8A258] mb-4">{t("values.eyebrow")}</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[#EDE3CF] tracking-tight leading-[1.05]">
            {t("values.title_l1")}<br /><em className="text-[#C8A258] not-italic">{t("values.title_l2")}</em>
          </h2>
          <p className="mt-5 text-[#EDE3CF]/70 leading-relaxed max-w-md">{t("values.intro")}</p>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-[#C8A258]/15 border border-[#C8A258]/15">
          {VALUE_ITEMS.map((v) => (
            <div key={v.key} className="bg-[#0B0A08] p-8 md:p-10" data-testid={`value-${v.key}`}>
              <v.icon className="w-7 h-7 text-[#C8A258] mb-5" />
              <h3 className="font-display text-2xl text-[#EDE3CF] mb-3">{t(`value.${v.key}.t`)}</h3>
              <p className="text-[#EDE3CF]/65 text-sm leading-relaxed">{t(`value.${v.key}.d`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Process = () => {
  const { t } = useT();
  return (
    <section id="processus" className="py-24 md:py-32" style={{ background: "#15130E" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl mb-16">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C8A258] mb-4">{t("process.eyebrow")}</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[#EDE3CF] tracking-tight">{t("process.title")}</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-px bg-[#C8A258]/15 border border-[#C8A258]/15">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="bg-[#15130E] p-8 md:p-10 min-h-[260px] flex flex-col gap-6" data-testid={`process-step-0${n}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-5xl text-[#C8A258] leading-none">0{n}</span>
                <span className="w-8 h-px bg-[#C8A258]" />
              </div>
              <div>
                <h3 className="font-display text-xl text-[#EDE3CF] mb-2">{t(`step.${n}.t`)}</h3>
                <p className="text-sm text-[#EDE3CF]/65 leading-relaxed">{t(`step.${n}.d`)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Zones = () => {
  const { t } = useT();
  return (
    <section id="zones" className="py-20 md:py-24" style={{ background: "#0B0A08" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C8A258] mb-3">{t("zones.eyebrow")}</div>
          <h2 className="font-display text-4xl text-[#EDE3CF] tracking-tight">{t("zones.title")}</h2>
          <p className="mt-4 text-[#EDE3CF]/70 leading-relaxed max-w-lg">{t("zones.intro")}</p>
        </div>
        <ul className="md:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="zones-list">
          {[...ZONES, t("zone.rivenord"), t("zone.remote")].map((z) => (
            <li key={z} className="px-4 py-3 border border-[#C8A258]/20 rounded-sm text-sm text-[#EDE3CF] bg-[#15130E] flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#C8A258]" /> {z}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// =================== Contact form ===================
const MAX_PHOTOS = 10;

const ContactForm = () => {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: "", address: "", message: "" });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(t("form.toast_max_photos"));
      return;
    }
    setUploading(true);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} ${t("form.toast_too_big")}`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${API}/uploads`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => [...p, { url: `${BACKEND_URL}${data.url}`, name: file.name, apiUrl: data.url }]);
      } catch {
        toast.error(`${t("form.toast_upload_fail")}${file.name}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error(t("form.toast_required"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, photo_urls: photos.map((p) => p.apiUrl) });
      toast.success(t("form.toast_success"));
      setForm({ name: "", email: "", phone: "", service_type: "", address: "", message: "" });
      setPhotos([]);
    } catch {
      toast.error(t("form.toast_error") + CONTACT.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative overflow-hidden grain"
      style={{ background: "linear-gradient(160deg, #15130E 0%, #0B0A08 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C8A258] mb-4">{t("contact.eyebrow")}</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[#EDE3CF] tracking-tight leading-[1.05]">
            {t("contact.title_l1")} <em className="text-[#C8A258] not-italic">{t("contact.title_l2")}</em>
          </h2>
          <p className="mt-5 text-[#EDE3CF]/70 leading-relaxed max-w-md">{t("contact.intro")}</p>
          <div className="mt-10 space-y-4 text-[#EDE3CF]/85">
            <a href={CONTACT.phoneHref} className="flex items-center gap-3 hover:text-[#C8A258]" data-testid="contact-phone">
              <Phone className="w-4 h-4 text-[#C8A258]" /> {CONTACT.phone}
            </a>
            <a href={CONTACT.smsHref} className="flex items-center gap-3 hover:text-[#C8A258]" data-testid="contact-sms">
              <MessageSquare className="w-4 h-4 text-[#C8A258]" /> {t("contact.sms")}
            </a>
            <a href={CONTACT.emailHref} className="flex items-center gap-3 hover:text-[#C8A258] break-all" data-testid="contact-email">
              <Mail className="w-4 h-4 text-[#C8A258] shrink-0" /> {CONTACT.email}
            </a>
            <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-[#C8A258]" /> {t("contact.zones")}</div>
            <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#C8A258]" /> {t("contact.hours")}</div>
          </div>
        </div>

        <form onSubmit={onSubmit}
          className="md:col-span-7 bg-[#0B0A08] rounded-sm p-8 md:p-10 border border-[#C8A258]/20"
          data-testid="quote-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#EDE3CF] text-xs tracking-[0.2em] uppercase">{t("form.name")} *</Label>
              <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)}
                className="rounded-sm bg-[#15130E] border-[#C8A258]/20 text-[#EDE3CF] focus-visible:ring-[#C8A258] h-11"
                data-testid="form-input-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#EDE3CF] text-xs tracking-[0.2em] uppercase">{t("form.email")} *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                className="rounded-sm bg-[#15130E] border-[#C8A258]/20 text-[#EDE3CF] focus-visible:ring-[#C8A258] h-11"
                data-testid="form-input-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#EDE3CF] text-xs tracking-[0.2em] uppercase">{t("form.phone")} *</Label>
              <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                className="rounded-sm bg-[#15130E] border-[#C8A258]/20 text-[#EDE3CF] focus-visible:ring-[#C8A258] h-11"
                data-testid="form-input-phone" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#EDE3CF] text-xs tracking-[0.2em] uppercase">{t("form.service")} *</Label>
              <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                <SelectTrigger className="rounded-sm bg-[#15130E] border-[#C8A258]/20 text-[#EDE3CF] focus:ring-[#C8A258] h-11" data-testid="form-select-service">
                  <SelectValue placeholder={t("form.service_placeholder")} />
                </SelectTrigger>
                <SelectContent className="rounded-sm bg-[#15130E] border-[#C8A258]/20 text-[#EDE3CF]">
                  <SelectItem value="Sablage">{t("service.option.sablage")}</SelectItem>
                  <SelectItem value="Teinture & vernissage">{t("service.option.teinture")}</SelectItem>
                  <SelectItem value="Réparation de bois">{t("service.option.reparation")}</SelectItem>
                  <SelectItem value="Calfeutrage / chinking">{t("service.option.calfeutrage")}</SelectItem>
                  <SelectItem value="Restauration complète">{t("service.option.complete")}</SelectItem>
                  <SelectItem value="Autre">{t("service.option.other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="address" className="text-[#EDE3CF] text-xs tracking-[0.2em] uppercase">{t("form.address")}</Label>
              <Input id="address" value={form.address} onChange={(e) => update("address", e.target.value)}
                className="rounded-sm bg-[#15130E] border-[#C8A258]/20 text-[#EDE3CF] focus-visible:ring-[#C8A258] h-11"
                data-testid="form-input-address" />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="message" className="text-[#EDE3CF] text-xs tracking-[0.2em] uppercase">{t("form.message")}</Label>
              <Textarea id="message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)}
                placeholder={t("form.message_placeholder")}
                className="rounded-sm bg-[#15130E] border-[#C8A258]/20 text-[#EDE3CF] focus-visible:ring-[#C8A258] resize-none"
                data-testid="form-input-message" />
            </div>

            <div className="sm:col-span-2 space-y-3">
              <Label className="text-[#EDE3CF] text-xs tracking-[0.2em] uppercase">
                {t("form.photos")} ({photos.length}/{MAX_PHOTOS}) — {t("form.photos_optional")}
              </Label>
              <div className="border border-dashed border-[#C8A258]/30 rounded-sm p-5 bg-[#15130E]/60">
                <div className="flex flex-wrap gap-3 items-center">
                  <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={onFiles}
                    className="hidden" data-testid="form-input-photos" id="photo-upload" />
                  <Button type="button" variant="outline" disabled={uploading || photos.length >= MAX_PHOTOS}
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-sm border-[#C8A258]/40 bg-transparent text-[#C8A258] hover:bg-[#C8A258]/10"
                    data-testid="form-upload-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? t("form.uploading") : t("form.add_photos")}
                  </Button>
                  <span className="text-xs text-[#EDE3CF]/50">{t("form.photos_hint")}</span>
                </div>
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4">
                    {photos.map((p, i) => (
                      <div key={i} className="relative group aspect-square rounded-sm overflow-hidden border border-[#C8A258]/20">
                        <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 bg-[#0B0A08]/90 hover:bg-red-700 text-[#EDE3CF] rounded-sm p-1 opacity-80 hover:opacity-100"
                          data-testid={`remove-photo-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 items-center justify-between">
            <p className="text-xs text-[#EDE3CF]/55">{t("form.required_note")}</p>
            <Button type="submit" disabled={submitting || uploading}
              className="rounded-sm bg-[#C8A258] hover:bg-[#B0894A] text-[#0B0A08] h-12 px-7"
              data-testid="form-submit-btn">
              {submitting ? t("form.submitting") : t("form.submit")}
              {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useT();
  return (
    <footer className="py-14 border-t border-[#C8A258]/15" style={{ background: "#0B0A08" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO} alt="AGL" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display text-lg text-[#EDE3CF]">
                AGL <span className="text-[#C8A258]">Bois Rond</span> {t("nav.brand_suffix")}
              </div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#C8A258]/80">{t("nav.tagline")}</div>
            </div>
          </div>
          <p className="text-sm text-[#EDE3CF]/65 leading-relaxed max-w-sm">{t("footer.about")}</p>
        </div>
        <div>
          <h4 className="font-display text-lg text-[#C8A258] mb-3">{t("footer.services")}</h4>
          <ul className="space-y-2 text-sm text-[#EDE3CF]/70">
            <li>{t("footer.s1")}</li>
            <li>{t("footer.s2")}</li>
            <li>{t("footer.s3")}</li>
            <li>{t("footer.s4")}</li>
          </ul>
        </div>
        <div>
          <h4 className="font-display text-lg text-[#C8A258] mb-3">{t("footer.contact")}</h4>
          <ul className="space-y-2 text-sm text-[#EDE3CF]/70">
            <li><a href={CONTACT.phoneHref} className="hover:text-[#C8A258]">{CONTACT.phone}</a></li>
            <li><a href={CONTACT.smsHref} className="hover:text-[#C8A258]">{t("footer.sms")}</a></li>
            <li><a href={CONTACT.emailHref} className="hover:text-[#C8A258] break-all">{CONTACT.email}</a></li>
            <li>{t("contact.zones")}</li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 mt-10 pt-6 border-t border-[#C8A258]/10 flex flex-col sm:flex-row justify-between gap-2 text-xs text-[#EDE3CF]/50">
        <span>© {new Date().getFullYear()} AGL Bois Rond Inc. — {t("footer.rights")}</span>
        <span className="italic text-[#C8A258]/70">{t("footer.tagline")}</span>
      </div>
    </footer>
  );
};

export default function Home() {
  useEffect(() => { axios.get(`${API}/`).catch(() => {}); }, []);
  return (
    <div className="min-h-screen" style={{ background: "#0B0A08" }}>
      <Nav />
      <main>
        <Hero />
        <Services />
        <VerminPrevention />
        <Gallery />
        <Values />
        <Process />
        <Zones />
        <ContactForm />
      </main>
      <Footer />
    </div>
  );
}
